import { FC, useCallback, useMemo } from "react";
import uuid from "src/utils/jsHelpers/uuid";
import {
  gridFilterModelSelector,
  GridRenderHeaderFilterProps,
  useGridApiContext,
  useGridRootProps,
  useGridSelector,
} from "@mui/x-data-grid-pro";
import { Autocomplete, Checkbox, TextField } from "@mui/material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import __ from "src/utils/Translations";

interface Option {
  label: string;
  value: string | number;
}

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const MultiSelectFilter: FC<
  GridRenderHeaderFilterProps & {
    options: Option[];
  }
> = ({ colDef, inputRef, options }) => {
  const rootProps = useGridRootProps();
  const apiRef = useGridApiContext();
  const filterModel = useGridSelector(apiRef, gridFilterModelSelector);

  const currentFieldFilters = useMemo(
    () => filterModel.items?.filter(({ field }) => field === colDef.field),
    [colDef.field, filterModel.items]
  );

  const selectedValue = useMemo(
    () => currentFieldFilters[0]?.value ?? [],
    [currentFieldFilters]
  );

  const handleChange = useCallback(
    (value: unknown) => {
      const currentValue = currentFieldFilters[0];
      const newFilter = {
        ...(currentValue || { id: uuid(), field: colDef.field }),
        operator: "isAnyOf",
        value,
      };
      apiRef.current?.upsertFilterItem(newFilter);
    },
    [apiRef, colDef.field, currentFieldFilters]
  );

  return (
    <>
      <Autocomplete
        value={selectedValue}
        onChange={(_event, value) => handleChange(value)}
        options={options}
        getOptionLabel={(option) => option.label}
        multiple
        disableCloseOnSelect
        fullWidth
        limitTags={1}
        size="small"
        sx={{
          "&:not(.Mui-focused) .MuiInputBase-formControl": {
            flexWrap: "nowrap",
          },
        }}
        renderOption={({ key, ...optionProps }, option, { selected }) => (
          <li key={key} {...optionProps}>
            <Checkbox
              icon={icon}
              checkedIcon={checkedIcon}
              style={{ marginRight: 8 }}
              checked={selected}
            />
            {option.label}
          </li>
        )}
        renderValue={(currentValue, getProps) =>
          currentValue.map((option, index) => {
            const { key, ...tagProps } = getProps({ index });
            return (
              <rootProps.slots.baseChip
                key={key}
                variant="outlined"
                size="small"
                label={option.label}
                {...tagProps}
              />
            );
          })
        }
        renderInput={({
          inputProps,
          InputProps,
          InputLabelProps,
          ...inputRest
        }) => (
          <TextField
            {...inputRest}
            label={__("Filtruj wg")}
            slotProps={{
              htmlInput: {
                ...inputProps,
              },
              input: {
                ...InputProps,
              },
              inputLabel: {
                ...InputLabelProps,
                shrink: true,
              },
            }}
            inputRef={inputRef}
            type="text"
            variant="outlined"
          />
        )}
        slotProps={{
          popper: { style: { width: "fit-content" } },
        }}
      />
    </>
  );
};

export default MultiSelectFilter;
